/**
 * The provider/model catalog the admin console publishes for the chat
 * composer. Fetched once per session and shared by the composer, the settings
 * modal and the helpers in utils/aiModelSettings that resolve a selection.
 *
 * `catalog` stays null until the first successful load, and stays at its last
 * good value if a later reload fails: a hiccup on the status call must not
 * turn the selector back into the built-in list mid-session.
 */

import { create } from 'zustand';
import { loadModelCatalog } from '../services/geminiService';

export interface ChatCatalogProvider {
  provider: string;
  label: string;
  baseUrl: string | null;
  visible: boolean;
}

export interface ChatCatalogModel {
  provider: string;
  model: string;
  label: string | null;
  visible: boolean;
}

export interface ChatCatalog {
  /** False only while no provider row exists at all — the "never set up" case. */
  configured: boolean;
  providers: ChatCatalogProvider[];
  models: ChatCatalogModel[];
}

export type ChatCatalogLoadState = 'idle' | 'loading' | 'ready' | 'failed';

interface ChatCatalogState {
  catalog: ChatCatalog | null;
  loadState: ChatCatalogLoadState;
  /** Loads once; `force` reloads after the admin console changed the catalog. */
  load(options?: { force?: boolean }): Promise<void>;
}

let inFlight: Promise<void> | null = null;

export const useChatCatalogStore = create<ChatCatalogState>((set, get) => ({
  catalog: null,
  loadState: 'idle',
  load: async (options = {}) => {
    if (inFlight) return inFlight;
    if (get().loadState === 'ready' && !options.force) return;

    set({ loadState: 'loading' });
    inFlight = (async () => {
      try {
        const catalog = await loadModelCatalog();
        if (catalog) set({ catalog, loadState: 'ready' });
        else set({ loadState: 'failed' });
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  },
}));
